import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  containRect,
  pointerToContainNorm,
  pointerToVideoNorm,
  toContainPx,
} from "../../utils/videoContainCoords";
import "./poseOverlays.css";

const LABELS = ["TL", "TR", "BR", "BL"];

function validCorners(corners) {
  return (
    Array.isArray(corners) &&
    corners.length === 4 &&
    corners.every(
      (c) =>
        c &&
        Number.isFinite(Number(c.x)) &&
        Number.isFinite(Number(c.y)) &&
        Number(c.x) >= 0 &&
        Number(c.x) <= 1 &&
        Number(c.y) >= 0 &&
        Number(c.y) <= 1
    )
  );
}

/**
 * 4-corner table calibration on a paused video frame.
 * Uses the same coordinate model as PlayerCalibrationOverlay and <video>
 * (full box + object-fit contain + CSS rotate/flip).
 */
export function TableCornerCalibrationOverlay({
  initialCorners = null,
  onSave,
  onCancel,
  saving = false,
  continueLabel = null,
  videoWidth = 0,
  videoHeight = 0,
  contentTransform = null,
  toolbarContainer = null,
}) {
  const { t } = useTranslation();
  const stageRef = useRef(null);
  const svgRef = useRef(null);
  const [stageSize, setStageSize] = useState({ w: 0, h: 0 });
  const [corners, setCorners] = useState(() =>
    validCorners(initialCorners)
      ? initialCorners.map((c) => ({ x: Number(c.x), y: Number(c.y) }))
      : []
  );
  const [dragIndex, setDragIndex] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (validCorners(initialCorners) && corners.length === 0) {
      setCorners(
        initialCorners.map((c) => ({ x: Number(c.x), y: Number(c.y) }))
      );
    }
  }, [initialCorners, corners.length]);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const measure = () => {
      setStageSize({ w: el.clientWidth, h: el.clientHeight });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const vw = Math.max(1, videoWidth || stageSize.w || 1);
  const vh = Math.max(1, videoHeight || stageSize.h || 1);

  const contain = useMemo(
    () =>
      containRect(
        Math.max(1, stageSize.w),
        Math.max(1, stageSize.h),
        vw,
        vh
      ),
    [stageSize, vw, vh]
  );

  const vbW = Math.max(1, stageSize.w);
  const vbH = Math.max(1, stageSize.h);

  const orientStyle = useMemo(() => {
    const base = { position: "absolute", inset: 0 };
    if (!contentTransform) return base;
    return {
      ...base,
      transform: contentTransform,
      transformOrigin: "center center",
    };
  }, [contentTransform]);

  const stepLabel = useMemo(() => {
    if (corners.length >= 4) {
      return t("poseCalibrateDragHint", "Drag handles to adjust, then continue.");
    }
    return t(
      "poseCalibrateTapHint",
      "Tap corner {{label}} ({{n}}/4) — TL → TR → BR → BL",
      { label: LABELS[corners.length], n: corners.length + 1 }
    );
  }, [corners.length, t]);

  const toNorm = useCallback(
    (e) => {
      const viaSvg = pointerToContainNorm(e, svgRef.current, vw, vh);
      if (viaSvg.inside) return viaSvg;
      // Fallback when CTM / letterbox math rejects a valid tap on the stage.
      const viaStage = pointerToVideoNorm(e, stageRef.current, vw, vh);
      if (viaStage.inside) return viaStage;
      // Near-miss on the video edge — still accept and clamp.
      const candidate = viaStage.layout ? viaStage : viaSvg;
      if (
        Number.isFinite(candidate.x) &&
        Number.isFinite(candidate.y) &&
        candidate.x >= -0.08 &&
        candidate.x <= 1.08 &&
        candidate.y >= -0.08 &&
        candidate.y <= 1.08
      ) {
        return {
          x: Math.min(1, Math.max(0, candidate.x)),
          y: Math.min(1, Math.max(0, candidate.y)),
          inside: true,
        };
      }
      return { x: 0, y: 0, inside: false };
    },
    [vw, vh]
  );

  const onPointerDown = (e) => {
    if (saving) return;
    e.preventDefault();
    e.stopPropagation();
    const pt = toNorm(e);
    if (!pt.inside) return;
    try {
      (e.currentTarget || svgRef.current || stageRef.current)?.setPointerCapture?.(
        e.pointerId
      );
    } catch {
      /* ignore */
    }
    const hit = corners.findIndex((c) => {
      const dx = c.x - pt.x;
      const dy = c.y - pt.y;
      return Math.hypot(dx, dy) < 0.05;
    });
    if (hit >= 0) {
      setDragIndex(hit);
      return;
    }
    if (corners.length < 4) {
      setCorners((prev) => [...prev, { x: pt.x, y: pt.y }]);
      setError("");
    }
  };

  const onPointerMove = (e) => {
    if (dragIndex == null || saving) return;
    e.preventDefault();
    e.stopPropagation();
    const pt = toNorm(e);
    if (!pt.inside && !Number.isFinite(pt.x)) return;
    const x = Math.min(1, Math.max(0, pt.x));
    const y = Math.min(1, Math.max(0, pt.y));
    setCorners((prev) =>
      prev.map((c, i) => (i === dragIndex ? { x, y } : c))
    );
  };

  const onPointerUp = (e) => {
    if (e) e.stopPropagation();
    setDragIndex(null);
  };

  const handleSave = async () => {
    if (corners.length !== 4) {
      setError(t("poseCalibrateNeedFour", "Place all 4 table corners."));
      return;
    }
    setError("");
    try {
      await onSave?.(corners);
    } catch (err) {
      setError(err?.message || "Save failed");
    }
  };

  const strokeW = Math.max(2, Math.min(contain.w, contain.h) * 0.003);
  const dotR = Math.max(6, Math.min(contain.w, contain.h) * 0.018);

  const toolbar = (
    <div className="table-calib-overlay__toolbar">
      <p className="table-calib-overlay__hint">{stepLabel}</p>
      {error ? <p className="table-calib-overlay__error">{error}</p> : null}
      <div className="table-calib-overlay__actions">
        <button
          type="button"
          className="table-calib-overlay__btn"
          onClick={() => {
            setCorners([]);
            setError("");
          }}
          disabled={saving}
        >
          {t("reset", "Reset")}
        </button>
        <button
          type="button"
          className="table-calib-overlay__btn"
          onClick={onCancel}
          disabled={saving}
        >
          {t("cancel", "Cancel")}
        </button>
        <button
          type="button"
          className="table-calib-overlay__btn table-calib-overlay__btn--primary"
          onClick={handleSave}
          disabled={saving || corners.length !== 4}
        >
          {saving
            ? t("saving", "Saving…")
            : continueLabel || t("poseCalibrateSave", "Save corners")}
        </button>
      </div>
    </div>
  );

  const pointerHandlers = {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel: onPointerUp,
  };

  return (
    <>
      <div className="table-calib-overlay table-calib-overlay--draw">
        <div
          ref={stageRef}
          className="table-calib-overlay__stage"
          {...pointerHandlers}
        >
          <div className="table-calib-overlay__orient" style={orientStyle}>
            <svg
              ref={svgRef}
              className="table-calib-overlay__svg table-calib-overlay__svg--hit"
              viewBox={`0 0 ${vbW} ${vbH}`}
              preserveAspectRatio="none"
              {...pointerHandlers}
            >
              <rect
                x={0}
                y={0}
                width={vbW}
                height={vbH}
                fill="rgba(0,0,0,0.01)"
                pointerEvents="all"
              />
              {corners.length >= 2 && (
                <polyline
                  points={corners
                    .map((c) => {
                      const p = toContainPx(c.x, c.y, contain);
                      return `${p.x},${p.y}`;
                    })
                    .join(" ")}
                  fill="none"
                  stroke="rgba(255,220,80,0.9)"
                  strokeWidth={strokeW}
                  pointerEvents="none"
                />
              )}
              {corners.length === 4 && (
                <polygon
                  points={corners
                    .map((c) => {
                      const p = toContainPx(c.x, c.y, contain);
                      return `${p.x},${p.y}`;
                    })
                    .join(" ")}
                  fill="rgba(255,220,80,0.12)"
                  stroke="rgba(255,220,80,0.95)"
                  strokeWidth={strokeW}
                  pointerEvents="none"
                />
              )}
              {corners.map((c, i) => {
                const p = toContainPx(c.x, c.y, contain);
                return (
                  <g key={i} pointerEvents="none">
                    <circle
                      cx={p.x}
                      cy={p.y}
                      r={dotR}
                      fill="#FFDC50"
                      stroke="#111"
                      strokeWidth={strokeW * 0.75}
                    />
                    <text
                      x={p.x + dotR + 4}
                      y={p.y - dotR * 0.5}
                      fill="#FFDC50"
                      fontSize={Math.max(11, dotR * 1.2)}
                      fontFamily="sans-serif"
                      fontWeight="700"
                    >
                      {LABELS[i]}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>
        </div>
      </div>
      {toolbarContainer ? (
        createPortal(toolbar, toolbarContainer)
      ) : (
        <div className="table-calib-overlay__toolbar-fallback">{toolbar}</div>
      )}
    </>
  );
}

export default TableCornerCalibrationOverlay;
